import { useLocaleState, useTranslate } from "ra-core";
import type { CSSProperties } from "react";

import { formatDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

import { pieceDate } from "./calendar";
import { STATUS_COLOR } from "./constants";
import { useMarketing } from "./context";
import { findById } from "./hooks";
import { isOverdue } from "./pipeline";
import type { ContentItem } from "./types";
import { ChannelLabel, FormatChip, PillarChips, StatusBadge } from "./ui";

/** A piece as a tappable row: time, title, channel, format, status. */
export const PieceRow = ({
  piece,
  showDate = true,
  index = 0,
}: {
  piece: ContentItem;
  /** Day + time (lists) or time only (a day's list). */
  showDate?: boolean;
  index?: number;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { data, openPiece } = useMarketing();
  const date = pieceDate(piece);
  const channel = findById(data.channels, piece.channel_id);
  const siblings = data.pillars.filter(
    (pillar) => String(pillar.plan_id) === String(piece.plan_id),
  );
  const pillars = [piece.pillar_id, piece.secondary_pillar_id]
    .map((id) => findById(data.pillars, id))
    .filter((pillar) => pillar != null);
  const overdue = isOverdue(piece, new Date());

  return (
    <li className="mi-rise" style={{ "--mi-index": index } as CSSProperties}>
      <button
        type="button"
        onClick={() => openPiece(piece.id)}
        className="mk-row group"
        style={
          {
            "--mk-status": STATUS_COLOR[piece.status] ?? STATUS_COLOR.idea,
          } as CSSProperties
        }
      >
        <span className="mk-row__when" aria-hidden={!date}>
          {date ? (
            <>
              {showDate ? (
                <span className="mk-row__day first-letter:uppercase">
                  {formatDate(date, locale, {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              ) : null}
              <span className="mk-row__time">
                {formatDate(date, locale, {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </>
          ) : (
            <span className="mk-row__time">—</span>
          )}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1.5 text-left">
          <span className="text-[15px] leading-snug font-medium [overflow-wrap:anywhere] group-hover:text-primary md:text-sm">
            {piece.title}
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            <ChannelLabel channel={channel} />
            <FormatChip format={piece.format} />
            <PillarChips pillars={pillars} siblings={siblings} />
            <StatusBadge status={piece.status} />
            {overdue ? (
              <span className={cn("mk-overdue")}>
                {translate("marketing.common.overdue")}
              </span>
            ) : null}
          </span>
        </span>
      </button>
    </li>
  );
};
